import { MarketButton } from '@market/react';
import { useTranslation } from 'react-i18next';
import { BankingModalPartial } from '@squareup/banking-shared-ui';
import { withI18n } from '@squareup/service-i18n/components';

export enum OptType {
  OPT_IN = 'OPT_IN',
  OPT_OUT = 'OPT_OUT',
}

export enum ModalStatus {
  CONFIRMATION = 'confirmation',
  SUCCESS = 'success',
  ERROR = 'error',
}

export interface OptConfirmationModalProps {
  readonly active: boolean;
  readonly optType: OptType;
  readonly platformLabel: string;
  readonly status?: ModalStatus;
  readonly loading?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export const OptConfirmationModal = ({
  active,
  optType,
  platformLabel,
  status = ModalStatus.CONFIRMATION,
  loading = false,
  onConfirm,
  onCancel,
}: OptConfirmationModalProps) => {
  const { t } = useTranslation();

  const optTypeKey = optType === OptType.OPT_IN ? 'optIn' : 'optOut';

  const doneButton = (
    <MarketButton key="done_button" rank="primary" onClick={onCancel}>
      {t('bankingInstantPayouts.optConfirmationModal.done')}
    </MarketButton>
  );

  const getModalProps = () => {
    if (status === ModalStatus.SUCCESS) {
      return {
        title: t(`bankingInstantPayouts.optConfirmationModal.${optTypeKey}.success.title`, { platformLabel }),
        content: t(`bankingInstantPayouts.optConfirmationModal.${optTypeKey}.success.description`, { platformLabel }),
        buttons: [doneButton],
        onModalClose: onCancel,
      };
    }

    if (status === ModalStatus.ERROR) {
      return {
        title: t('bankingInstantPayouts.optConfirmationModal.error.title'),
        content: t('bankingInstantPayouts.optConfirmationModal.error.description'),
        buttons: [doneButton],
        onModalClose: onCancel,
      };
    }

    return {
      title: t(`bankingInstantPayouts.optConfirmationModal.${optTypeKey}.title`, { platformLabel }),
      content: t(`bankingInstantPayouts.optConfirmationModal.${optTypeKey}.description`, { platformLabel }),
      buttons: [
        <MarketButton
          key="confirm_button"
          rank="primary"
          isLoading={loading}
          onClick={onConfirm}
        >
          {t(`bankingInstantPayouts.optConfirmationModal.${optTypeKey}.confirm`)}
        </MarketButton>,
        <MarketButton key="cancel_button" disabled={loading} onClick={onCancel}>
          {t('bankingInstantPayouts.optConfirmationModal.cancel')}
        </MarketButton>,
      ],
      onModalClose: onCancel,
    };
  };

  return <BankingModalPartial active={active} {...getModalProps()} />;
};

export const OptConfirmationModalI18n = withI18n(OptConfirmationModal);
